import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AppStateService } from '../../services/app-state.service';
import { MOCK_WALLPAPERS } from '../../models/constants';
import { CartItem, Wallpaper } from '../../models/wallpaper.model';

@Component({
  selector: 'app-cart',
  templateUrl: './cart.component.html'
})
export class CartComponent implements OnInit, OnDestroy {
  items: CartItem[] = [];

  /**
   * True until the cart has finished its first load. Without it the template read
   * `items.length === 0` while the fetch was still in flight and flashed the
   * "empty cart" screen for a second before the rows arrived. Driven by
   * `AppStateService.cartLoaded$` (a BehaviorSubject, so it reports the current
   * value the moment we subscribe — if the header already loaded the cart there is
   * no loader flash at all). The empty state now waits for this to be false.
   */
  cartLoading = true;

  promoCode = '';
  promoApplied: string | null = null;
  promoError = '';

  private readonly validPromoCodes: Record<string, number> = { 'FIRST10': 10, 'LOYAL15': 15, 'WALLS20': 20 };
  private sub = new Subscription();

  /**
   * True while ANY quantity change is being processed — from the click until the
   * reload has repainted the cart and Order Summary. Mirrors the shared
   * `AppStateService.cartBusy$`. While it is on, every +/- and Remove control is
   * disabled and a blocking overlay covers the cart, so a user cannot fire a
   * second, overlapping update whose (possibly out-of-order) response would leave
   * the UI stale. A single global flag — not a per-row one — because the reload it
   * waits on refetches the WHOLE cart, so no row is safe to touch until it lands.
   */
  cartBusy = false;

  /**
   * Whether the +/- controls are offered. ON.
   *
   * `updateQuantity()` below is correct and the endpoint behind it works — it
   * persists, it is owner-scoped, and GET /api/site/cart scales the line exactly
   * (verified 1x/2x/3x). For lines written by the CURRENT site flow, quantity is
   * money-safe: WallpaperCartService::setQuantity() re-resolves installation from
   * the scaled `areaSqft` (installCharge = max(area × rate, min)), so extra rolls
   * are installed for the right area.
   *
   * Known caveat (pre-existing, NOT this control's concern): a LEGACY old-shape
   * `pricing_detail` row has no `areaSqft` and carries `installation_amount`
   * rather than `installCharge`, so setQuantity's `$wantsInstall` gate reads false
   * and installation is not re-resolved for it. That is a backend/data-shape issue
   * for legacy rows only and is tracked separately from this UI.
   *
   * The concurrency hazard of these buttons — rapid clicks whose out-of-order
   * reloads left the cart/Order Summary stale — is handled by the shared
   * `cartBusy$` flag (blocking overlay + disabled controls) and the newest-wins
   * reload guard in AppStateService.loadCartFromApi().
   */
  readonly quantityEditable = true;

  constructor(public state: AppStateService, public router: Router) {}

  ngOnInit() {
    this.sub.add(this.state.cart$.subscribe(c => this.items = c));
    this.sub.add(this.state.cartBusy$.subscribe(b => this.cartBusy = b));
    // Drives the initial loader: false once the cart has loaded (empty or not), so
    // the "empty cart" screen only shows after a real, finished fetch.
    this.sub.add(this.state.cartLoaded$.subscribe(loaded => this.cartLoading = !loaded));
    // Ensure the cart is actually fetched on a direct visit to /cart. A no-op if the
    // header already loaded it (loadCartFromApi early-returns when loaded && !force).
    this.state.loadCartFromApi();
  }

  ngOnDestroy() { this.sub.unsubscribe(); }

  get subtotal(): number { return this.items.reduce((s, i) => s + i.price * i.quantity, 0); }
  get totalItems(): number { return this.items.reduce((s, i) => s + i.quantity, 0); }
  get promoDiscount(): number { return this.promoApplied ? (this.validPromoCodes[this.promoApplied] || 0) : 0; }
  get discountAmount(): number { return Math.round(this.subtotal * this.promoDiscount / 100); }
  get total(): number { return this.subtotal - this.discountAmount; }
  get freeShippingProgress(): number { return Math.min(100, (this.subtotal / 500) * 100); }
  get amountToFreeShipping(): number { return Math.max(0, 500 - this.subtotal); }

  get recommendations(): Wallpaper[] {
    if (this.items.length === 0) return MOCK_WALLPAPERS.slice(0, 4);
    const cartCategories = new Set(this.items.map(i => i.category));
    const cartIds = new Set(this.items.map(i => i.id));
    const byCategory = MOCK_WALLPAPERS.filter(wp => !cartIds.has(wp.id) && cartCategories.has(wp.category)).slice(0, 4);
    return byCategory.length > 0 ? byCategory : MOCK_WALLPAPERS.slice(0, 4);
  }

  applyPromo() {
    const code = this.promoCode.trim().toUpperCase();
    if (this.validPromoCodes[code]) {
      this.promoApplied = code;
      this.promoError = '';
    } else {
      this.promoError = 'Invalid promo code';
      this.promoApplied = null;
    }
  }

  /**
   * Change a line's quantity — on the SERVER.
   *
   * This used to call `state.updateCartQuantity()`, which edited nothing but the
   * local BehaviorSubject: the number on screen moved, no request was sent, and
   * the next cart load put the old quantity back. Anyone who used these buttons
   * was editing a picture of their cart, not their cart.
   *
   * It is also half of why the header badge could be watched jumping — the local
   * figure was a guess, and the API answer overwrote it a moment later.
   *
   * Sends the ABSOLUTE quantity, not the delta: the server rescales a whole-line
   * price from it, so two clicks racing each other must not each apply a step.
   */
  updateQuantity(item: any, delta: number) {
    const cartId = Number(item.cart_id || item.id);
    const current = Number(item.quantity) || 1;
    const next = Math.max(1, current + delta);
    // One update at a time: `cartBusy` stays on until the reload has repainted, so
    // a second click cannot start a request that races the first. This is what
    // prevents the rapid-click stale-UI: no overlapping reloads, and the service's
    // newest-wins guard is the backstop for any that still overlap.
    if (!cartId || next === current || this.cartBusy) return;

    this.state.beginCartMutation();
    this.state.apiUpdateQuantity(cartId, next).subscribe({
      next: () => {
        // Reload rather than patch locally: the line total, the tax split and the
        // cart's discount eligibility all move with the quantity, and only the
        // server knows what they become. loadCartFromApi() clears cartBusy once the
        // fresh state is painted.
        this.state.loadCartFromApi(true);
      },
      error: () => {
        this.state.showToast('Could not update the quantity. Please try again.', 'info');
        // Re-sync to the true persisted state (and clear the spinner) after a fail.
        this.state.loadCartFromApi(true);
      },
    });
  }

  /**
   * Remove a line — on the SERVER.
   *
   * `state.removeFromCart()` only filtered the local list, so the item was back
   * on the next load. `apiRemoveCartItem` does the delete and drops the row
   * locally straight away, so the reload here is only needed when it fails.
   */
  removeItem(item: any) {
    const cartId = Number(item.cart_id || item.id);
    // Don't remove a row while a quantity reload is mid-flight — it would race the
    // refetch and could reappear from the in-flight response.
    if (!cartId || this.cartBusy) return;

    this.state.apiRemoveCartItem(cartId).subscribe({
      next: () => this.state.showToast('Item removed from cart', 'cart'),
      error: () => {
        this.state.showToast('Could not remove the item. Please try again.', 'info');
        this.state.loadCartFromApi(true);
      },
    });
  }

  /**
   * A wallpaper cannot be added to the cart from here — it opens the product page.
   *
   * A cart line needs a wall size, a paper and a service choice; the PDP collects
   * those three and prices the result. This button used to call
   * `state.addToCart()`, which pushed the raw wallpaper into the cart stream with
   * no size, no paper, no price and no request behind it — the badge went up, the
   * next load dropped it, and nothing had ever been added. These recommendations
   * come from MOCK_WALLPAPERS, so the row was not even a real product.
   */
  addRecommendation(wp: Wallpaper) { this.router.navigate(['/pdp', wp.id]); }
  navigateToPDP(id: string) { this.router.navigate(['/pdp', id]); }
  navigateToListing() { this.router.navigate(['/listing']); }

  getDeliveryStart(): string {
    const d = new Date(); d.setDate(d.getDate() + 5);
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  }
  getDeliveryEnd(): string {
    const d = new Date(); d.setDate(d.getDate() + 7);
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  }
}
